import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { roleLabel } from '../admin/adminShared';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { toast, WorkflowNotices } from './notify';
import { LogOut, RefreshCw, Stethoscope, FlaskConical, ScanLine, Smile, Pill, Receipt, ShieldCheck, LayoutList, ArrowLeft, KeyRound, BarChart3, BedDouble, HelpCircle, Timer } from 'lucide-react';
import ChangePasswordDialog from '../../components/ChangePasswordDialog';
import PushToggle from '../../components/PushToggle';
import useWebSocket from '../../hooks/useWebSocket';
import ConsultationPanel from './ConsultationPanel';
import LabPanel from './LabPanel';
import RadiologyPanel from './RadiologyPanel';
import PharmacyPanel from './PharmacyPanel';
import BillingPanel from './BillingPanel';
import ApprovalsPanel from './ApprovalsPanel';
import AllVisitsPanel from './AllVisitsPanel';
import InsightsPanel from './InsightsPanel';
import CooldownPanel from './CooldownPanel';
import WardBoardPanel from './WardBoardPanel';
import { deptForRole, DOCTOR_ROLES, visitDept, roleDepts, roleCovers } from './VisitBits';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const playBeep = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g);
    g.connect(ctx.destination);
    o.frequency.value = 880;
    g.gain.value = 0.06;
    o.start();
    o.stop(ctx.currentTime + 0.18);
    o.onended = () => ctx.close();
  } catch (e) { /* audio not available */ }
};

export default function WorkflowPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = localStorage.getItem('token');
  const username = localStorage.getItem('username');
  const role = localStorage.getItem('role');
  const hospitalName = localStorage.getItem('hospital_name');
  const isSuperAdmin = username === 'superadmin' || (role === 'Admin' && hospitalName === 'System Administration');
  const isClinical = ['Consultant', 'Gynaecologist', 'Specialist', 'Pharmacist', 'Lab', 'Radiology'].includes(role);
  const myDept = deptForRole(role);
  const authCfg = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const [queue, setQueue] = useState({ visits: [], counts: {} });
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);

  const fetchQueue = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/visits/queue`, { headers: { Authorization: `Bearer ${token}` } });
      setQueue(res.data);
    } catch (e) {
      if (e.response?.status === 401) navigate('/login');
    }
  }, [token, navigate]);

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    fetchQueue();
    const t = setInterval(fetchQueue, 30000);
    return () => clearInterval(t);
  }, [fetchQueue, token, navigate]);

  // Live queue updates via WebSocket (polling remains as fallback)
  const notifyStatuses =
    DOCTOR_ROLES.includes(role) ? ['WAITING_CONSULTANT', 'CONSULTANT_REVIEW']
      : role === 'Lab' ? ['LAB_PENDING', 'DIAGNOSTICS_PENDING']
        : role === 'Radiology' ? ['RAD_PENDING', 'DIAGNOSTICS_PENDING']
          : role === 'Pharmacist' ? ['PHARMACY_PENDING']
            : ['Reception', 'Finance'].includes(role) ? ['READY_FOR_BILLING']
              : isSuperAdmin ? ['AWAITING_LAB_APPROVAL', 'AWAITING_RAD_APPROVAL', 'AWAITING_DENTAL_APPROVAL', 'AWAITING_SERVICE_APPROVAL', 'AWAITING_REFERRAL_APPROVAL', 'AWAITING_RX_APPROVAL'] : [];

  const handleWsMessage = useCallback((msg) => {
    if (msg.type === 'referral_decision') {
      const d = msg.data || {};
      if (isSuperAdmin || role === 'ViewOnly' || d.hospital_name === hospitalName) {
        if (d.decision === 'approved') toast.success(`External referral APPROVED for ${d.patient_name} — ${d.referral_number}. The referral letter can now be printed from All Visits.`, { duration: 10000 });
        else toast.error(`External referral REJECTED for ${d.patient_name}${d.reason ? ` — ${d.reason}` : ''}.`, { duration: 10000 });
      }
      fetchQueue();
      return;
    }
    if (msg.type === 'preauth_decision') {
      const d = msg.data || {};
      if (isSuperAdmin || role === 'ViewOnly' || d.hospital_name === hospitalName) {
        if (d.decision === 'approved') {
          toast.success(`Pre-authorization APPROVED for ${d.patient_name} — ${d.preauth_number}. The patient can proceed to the doctor.`, { duration: 10000 });
        } else {
          toast.error(`Pre-authorization REJECTED for ${d.patient_name}${d.reason ? ` — ${d.reason}` : ''}. The visit was cancelled.`, { duration: 10000 });
        }
      }
      fetchQueue();
      return;
    }
    if (msg.type !== 'visit_updated') return;
    const d = msg.data || {};
    fetchQueue();
    const relevantHospital = isSuperAdmin || role === 'ViewOnly' || d.hospital_name === hospitalName;
    const relevantDept = !myDept || !['WAITING_CONSULTANT', 'CONSULTANT_REVIEW'].includes(d.status) || !d.department || roleDepts(role).includes(d.department);
    if (relevantHospital && relevantDept && notifyStatuses.includes(d.status)) {
      playBeep();
      toast.info(`New patient in your queue: ${d.visit_id}`, {
        description: (d.status || '').replace(/_/g, ' ').toLowerCase(),
      });
    }
    // eslint-disable-next-line
  }, [fetchQueue]);

  const { isConnected } = useWebSocket(handleWsMessage);

  const counts = queue.counts || {};
  const inpatientPending = (field) => (queue.visits || []).filter((v) => v.status === 'ADMITTED' && (v[field] || []).some((o) => o.status === 'pending')).length;
  const consultVisits = (queue.visits || []).filter((v) => ['WAITING_CONSULTANT', 'IN_CONSULTATION', 'CONSULTANT_REVIEW', 'ADMITTED'].includes(v.status) && roleCovers(role, v));
  const tabs = [
    { id: 'consultation', label: 'Consultation', icon: Stethoscope, badge: consultVisits.length, show: isSuperAdmin || [...DOCTOR_ROLES, 'Admin', 'ViewOnly'].includes(role) },
    { id: 'approvals', label: 'Approvals', icon: ShieldCheck, badge: (counts.AWAITING_LAB_APPROVAL || 0) + (counts.AWAITING_RAD_APPROVAL || 0) + (counts.AWAITING_DENTAL_APPROVAL || 0) + (counts.AWAITING_SERVICE_APPROVAL || 0) + (counts.AWAITING_REFERRAL_APPROVAL || 0) + (counts.AWAITING_RX_APPROVAL || 0) + (counts.AWAITING_PREAUTH || 0) + (counts.AWAITING_ADMISSION_APPROVAL || 0) + (counts.PENDING_REFERRALS || 0), show: isSuperAdmin },
    { id: 'lab', label: 'Laboratory', icon: FlaskConical, badge: (counts.LAB_PENDING || 0) + (counts.DIAGNOSTICS_PENDING || 0) + inpatientPending('lab_orders'), show: isSuperAdmin || ['Lab', 'Admin', 'ViewOnly'].includes(role) },
    { id: 'radiology', label: 'Radiology', icon: ScanLine, badge: (counts.RAD_PENDING || 0) + (counts.DIAGNOSTICS_PENDING || 0) + (counts.AWAITING_RAD_APPROVAL || 0) + inpatientPending('rad_orders'), show: isSuperAdmin || ['Radiology', 'Admin', 'ViewOnly'].includes(role) },
    { id: 'pharmacy', label: 'Pharmacy', icon: Pill, badge: (counts.PHARMACY_PENDING || 0) + inpatientPending('prescriptions'), show: isSuperAdmin || ['Pharmacist', 'Admin', 'ViewOnly'].includes(role) },
    { id: 'billing', label: 'Billing', icon: Receipt, badge: counts.READY_FOR_BILLING || 0, show: isSuperAdmin || ['Reception', 'Finance', 'Admin', 'ViewOnly'].includes(role) },
    { id: 'ward', label: 'Ward', icon: BedDouble, badge: (counts.ADMITTED || 0), show: isSuperAdmin || [...DOCTOR_ROLES, 'Admin', 'Finance', 'Reception', 'Pharmacist', 'Lab', 'ViewOnly'].includes(role) },
    { id: 'insights', label: 'Insights', icon: BarChart3, badge: 0, show: isSuperAdmin || ['Admin', 'Finance', 'ViewOnly'].includes(role) },
    { id: 'cooldowns', label: '14DaysLimit', icon: Timer, badge: 0, show: isSuperAdmin || (role === 'ViewOnly' && hospitalName === 'System Administration') },
    { id: 'all', label: 'All Visits', icon: LayoutList, badge: (queue.visits || []).length, show: true },
  ].filter((t) => t.show);

  const defaultTab =
    DOCTOR_ROLES.includes(role) ? 'consultation'
      : role === 'Lab' ? 'lab'
        : role === 'Radiology' ? 'radiology'
          : role === 'Pharmacist' ? 'pharmacy'
            : ['Reception', 'Finance'].includes(role) ? 'billing'
              : tabs[0]?.id || 'all';
  const requestedTab = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(requestedTab && tabs.some((t) => t.id === requestedTab) ? requestedTab : defaultTab);

  const handleLogout = () => {
    localStorage.clear();
    navigate('/login');
  };

  const panelProps = { visits: queue.visits || [], refresh: fetchQueue, authCfg, role, isSuperAdmin, username };

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #e3f2fd 0%, #f0f4f8 100%)' }}>
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="w-full lg:w-[90%] mx-auto px-3 sm:px-4 py-3 sm:py-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="min-w-0 flex-1 basis-full sm:basis-auto">
              <h1 className="text-lg sm:text-2xl font-bold text-gray-800 flex items-center gap-2">
                <Stethoscope className="w-5 h-5 sm:w-6 sm:h-6 text-teal-600" />
                Patient Workflow
              </h1>
              <p className="text-xs sm:text-sm text-gray-600 mt-0.5 flex items-center gap-2 min-w-0">
                <span className="truncate">{hospitalName} • {username} • {roleLabel(role, localStorage.getItem('specialty'))}</span>
                <span className="inline-flex items-center gap-1 shrink-0" data-testid="workflow-live-indicator">
                  <span className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500 animate-pulse' : 'bg-gray-300'}`} />
                  <span className={isConnected ? 'text-green-600 font-medium' : 'text-gray-400'}>{isConnected ? 'Live' : 'Offline'}</span>
                </span>
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0 w-full sm:w-auto justify-between sm:justify-end">
              {(isSuperAdmin || (role === 'ViewOnly' && hospitalName === 'System Administration')) && <PushToggle authCfg={authCfg} />}
              <Button variant="outline" size="sm" onClick={fetchQueue} data-testid="workflow-refresh-button" className="border-gray-300">
                <RefreshCw className="w-4 h-4" />
                <span className="hidden sm:inline ml-1">Refresh</span>
              </Button>
              {!isClinical && (
                <Button variant="outline" size="sm" onClick={() => navigate('/dashboard')} data-testid="workflow-back-dashboard" className="border-gray-300">
                  <ArrowLeft className="w-4 h-4" />
                  <span className="hidden sm:inline ml-1">Dashboard</span>
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={() => navigate('/help?section=workflow')} data-testid="workflow-help" className="border-gray-300" title="How to use the Patient Workflow">
                <HelpCircle className="w-4 h-4" />
                <span className="hidden sm:inline ml-1">Help</span>
              </Button>
              <Button variant="outline" size="sm" onClick={() => setChangePasswordOpen(true)} data-testid="workflow-change-password" className="border-gray-300">
                <KeyRound className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={handleLogout} data-testid="workflow-logout" className="border-red-200 text-red-600 hover:bg-red-50">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>

          <div className={`flex flex-wrap lg:flex-nowrap gap-1 min-[1800px]:gap-2 mt-4 w-full ${tabs.length > 6 ? "" : "justify-center"}`} data-testid="workflow-tabs">
            {tabs.map((t) => {
              const Icon = t.icon;
              const active = activeTab === t.id;
              const compact = tabs.length > 6;
              return (
                <button
                  key={t.id}
                  onClick={() => setActiveTab(t.id)}
                  data-testid={`workflow-tab-${t.id}`}
                  title={t.label}
                  className={`${compact
                    ? 'flex-auto lg:min-w-0 gap-1.5 lg:gap-1 min-[1800px]:gap-2 px-2 lg:px-1.5 min-[1800px]:px-3 text-[13px] lg:text-xs min-[1800px]:text-sm'
                    : 'gap-1.5 px-4 text-[13px] 2xl:text-sm'} flex items-center justify-center py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
                    active ? 'bg-teal-600 text-white shadow' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <Icon className={`w-4 h-4 shrink-0 ${compact ? 'lg:hidden 2xl:block' : ''}`} />
                  <span className="truncate">{t.label}</span>
                  <span
                    data-testid={`workflow-tab-badge-${t.id}`}
                    className={`shrink-0 min-w-[1.5rem] text-center px-1.5 py-0.5 rounded-full text-xs font-bold ${t.badge > 0 ? '' : 'invisible'} ${active ? 'bg-white text-teal-700' : 'bg-teal-600 text-white'}`}
                  >
                    {t.badge > 0 ? t.badge : 0}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="w-full lg:w-[90%] mx-auto px-3 sm:px-4 py-4 sm:py-6">
        {activeTab === 'consultation' && <ConsultationPanel {...panelProps} />}
        {activeTab === 'approvals' && <ApprovalsPanel {...panelProps} />}
        {activeTab === 'lab' && <LabPanel {...panelProps} />}
        {activeTab === 'radiology' && <RadiologyPanel {...panelProps} />}
        {activeTab === 'pharmacy' && <PharmacyPanel {...panelProps} />}
        {activeTab === 'billing' && <BillingPanel {...panelProps} />}
        {activeTab === 'insights' && <InsightsPanel authCfg={authCfg} isSuperAdmin={isSuperAdmin} />}
        {activeTab === 'cooldowns' && <CooldownPanel authCfg={authCfg} isSuperAdmin={isSuperAdmin} />}
        {activeTab === 'ward' && <WardBoardPanel visits={queue.visits || []} authCfg={authCfg} isSuperAdmin={isSuperAdmin} onOpenPatient={DOCTOR_ROLES.includes(role) || isSuperAdmin ? () => setActiveTab('consultation') : null} />}
        {activeTab === 'all' && <AllVisitsPanel {...panelProps} />}
      </div>

      <ChangePasswordDialog open={changePasswordOpen} onOpenChange={setChangePasswordOpen} />
      <WorkflowNotices />
    </div>
  );
}
